import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../services/api';

const RecordPayment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searching, setSearching] = useState(false);
  const [formData, setFormData] = useState({
    amountPaid: '',
    paymentMode: 'CASH',
    feeCategory: 'TUITION',
    transactionId: '',
    remarks: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Auto-search if USN is in URL params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const usn = params.get('usn');
    if (usn) {
      setSearchQuery(usn);
      searchStudentByUSN(usn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const searchStudentByUSN = async (usn) => {
    try {
      setSearching(true);
      const response = await api.get(`/users/search?query=${usn}`);
      if (response.data.data.length === 0) {
        toast.error('Student not found');
        setStudents([]);
        setSelectedStudent(null);
      } else {
        setStudents(response.data.data);
        if (response.data.data.length === 1) {
          setSelectedStudent(response.data.data[0]);
        }
      }
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Failed to search student');
    } finally {
      setSearching(false);
    }
  };

  const searchStudent = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter USN or name');
      return;
    }

    try {
      setSearching(true);
      const response = await api.get(`/users/search?query=${searchQuery}`);
      if (response.data.data.length === 0) {
        toast.error('Student not found');
        setStudents([]);
        setSelectedStudent(null);
      } else {
        setStudents(response.data.data);
        if (response.data.data.length === 1) {
          setSelectedStudent(response.data.data[0]);
          toast.success('Student found');
        } else {
          toast.success(`Found ${response.data.data.length} students`);
        }
      }
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Failed to search student');
    } finally {
      setSearching(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedStudent) {
      toast.error('Please search and select a student first');
      return;
    }

    if (!formData.amountPaid || parseFloat(formData.amountPaid) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      setSubmitting(true);
      await api.post('/fees/payment', {
        userId: selectedStudent.id,
        ...formData,
        amountPaid: parseFloat(formData.amountPaid)
      });

      toast.success('Payment recorded successfully');
      
      // Navigate to logs page with USN
      navigate(`/fees/logs?usn=${selectedStudent.usn}`);
    } catch (error) {
      console.error('Payment recording failed:', error);
      toast.error(error.response?.data?.error || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Record Payment</h1>
          <p className="text-gray-600 mt-2">Record offline payment for a student</p>
        </div>

        {/* Student Search */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Search Student</h2>
          <div className="flex gap-3">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && searchStudent()}
              placeholder="Enter USN or Name..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={searchStudent}
              disabled={searching}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
            >
              {searching ? 'Searching...' : 'Search'}
            </button>
          </div>

          {students.length > 0 && (
            <div className="mt-4 space-y-2">
              {students.map((student) => (
                <div
                  key={student.id}
                  onClick={() => {
                    setSelectedStudent(student);
                    setStudents([student]);
                  }}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedStudent?.id === student.id
                      ? 'bg-blue-50 border-blue-500'
                      : 'bg-gray-50 border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <h3 className="font-semibold text-gray-900">{student.name}</h3>
                  <p className="text-sm text-gray-600">USN: {student.usn}</p>
                  <p className="text-sm text-gray-600">
                    {student.department} - Semester {student.semester?.replace('SEM_', '')}
                  </p>
                  <p className="text-sm text-gray-600">Email: {student.collegeEmail}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Payment Form */}
        {selectedStudent && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Details</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.amountPaid}
                    onChange={(e) => setFormData({ ...formData, amountPaid: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter amount"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Mode <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.paymentMode}
                    onChange={(e) => setFormData({ ...formData, paymentMode: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="CASH">Cash</option>
                    <option value="UPI">UPI</option>
                    <option value="CARD">Card</option>
                    <option value="NET_BANKING">Net Banking</option>
                    <option value="CHEQUE">Cheque</option>
                    <option value="DEMAND_DRAFT">Demand Draft</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fee Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.feeCategory}
                    onChange={(e) => setFormData({ ...formData, feeCategory: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="TUITION">Tuition Fee</option>
                    <option value="EXAM">Exam Fee</option>
                    <option value="LIBRARY">Library Fee</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Transaction ID (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.transactionId}
                    onChange={(e) => setFormData({ ...formData, transactionId: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter transaction ID"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Remarks (Optional)
                </label>
                <textarea
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Add any additional remarks..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedStudent(null);
                    setStudents([]);
                    setSearchQuery('');
                    setFormData({
                      amountPaid: '',
                      paymentMode: 'CASH',
                      feeCategory: 'TUITION',
                      transactionId: '',
                      remarks: ''
                    });
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
                >
                  {submitting ? 'Recording...' : 'Record Payment'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecordPayment;
